'use client';

/**
 * The email half of chat settings: which account `send_email` uses by default,
 * which signature it appends, and which linked Google Calendar Meridian reads.
 *
 * Save has two shapes and they are NOT symmetric:
 *  - clearing the default   → DELETE the preference, then a calendar-only PUT.
 *    No signature call (there is no account to attach one to) and no refetch.
 *  - setting a default      → PUT the preference (carrying the calendar choice),
 *    PUT the signature, then refetch so `is_default` reflects what was saved.
 */

import { useCallback, useEffect, useState } from 'react';
import { getAuthToken } from '../../lib/api/authenticated-fetch.js';
import {
  computeHasChanges,
  defaultSignatureId,
  connectionValue,
  parseConnectionValue,
  parseEmailSendContext,
  type DefaultEmailPreference,
  type EmailConnection,
  type SignatureOption,
} from './email-send-context.js';

function buildHeaders(): Record<string, string> {
  const token = getAuthToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

export interface EmailSendPreferences {
  connections: EmailConnection[];
  selectedValue: string;
  setSelectedValue: (value: string) => void;
  parsed: DefaultEmailPreference | null;
  signaturesForConnection: SignatureOption[];
  selectedSignatureId: string;
  setSelectedSignatureId: (value: string) => void;
  calendarUseSecondAccount: boolean;
  setCalendarUseSecondAccount: (value: boolean) => void;
  loading: boolean;
  saving: boolean;
  saved: boolean;
  error: string | null;
  hasChanges: boolean;
  save: () => Promise<void>;
}

export function useEmailSendPreferences({
  apiBaseUrl,
  open,
}: {
  apiBaseUrl: string;
  open: boolean;
}): EmailSendPreferences {
  const [connections, setConnections] = useState<EmailConnection[]>([]);
  const [signaturesByConnectionId, setSignaturesByConnectionId] = useState<
    Record<string, SignatureOption[]>
  >({});
  const [defaultPreference, setDefaultPreference] = useState<DefaultEmailPreference | null>(null);
  const [selectedValue, setSelectedValue] = useState<string>('');
  const [selectedSignatureId, setSelectedSignatureId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  /** Meridian + Unified Calendar: primary vs second linked Google account. */
  const [calendarUseSecondAccount, setCalendarUseSecondAccount] = useState(false);
  const [serverCalendarUseSecondAccount, setServerCalendarUseSecondAccount] = useState(false);

  const fetchContext = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${apiBaseUrl}/email-send-context`, { headers: buildHeaders() });
      if (!res.ok) throw new Error('Failed to load email connections');
      const context = parseEmailSendContext(await res.json());
      setConnections(context.connections);
      setSignaturesByConnectionId(context.signaturesByConnectionId);
      setCalendarUseSecondAccount(context.googleCalendarUseSecondAccount);
      setServerCalendarUseSecondAccount(context.googleCalendarUseSecondAccount);
      if (context.defaultPreference) {
        setDefaultPreference(context.defaultPreference);
        setSelectedValue(connectionValue(context.defaultPreference));
        setSelectedSignatureId(
          defaultSignatureId(
            context.signaturesByConnectionId[context.defaultPreference.connectionId]
          )
        );
      } else {
        setDefaultPreference(null);
        setSelectedValue('');
        setSelectedSignatureId('');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl]);

  useEffect(() => {
    if (open) fetchContext();
  }, [open, fetchContext]);

  const parsed = parseConnectionValue(selectedValue);

  // Changing the selected connection resets the signature to that connection's
  // current default — a signature belongs to one account.
  useEffect(() => {
    if (!parsed?.connectionId) {
      setSelectedSignatureId('');
      return;
    }
    setSelectedSignatureId(defaultSignatureId(signaturesByConnectionId[parsed.connectionId]));
  }, [parsed?.connectionId, signaturesByConnectionId]);

  const signaturesForConnection = parsed?.connectionId
    ? (signaturesByConnectionId[parsed.connectionId] ?? [])
    : [];
  const currentDefaultSignatureId = defaultSignatureId(signaturesForConnection);

  const save = useCallback(async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    const headers = buildHeaders();
    try {
      const connParsed = parseConnectionValue(selectedValue);
      if (!connParsed) {
        const res = await fetch(`${apiBaseUrl}/email-send-preference`, {
          method: 'DELETE',
          headers,
        });
        if (!res.ok) throw new Error('Failed to clear default');
        setDefaultPreference(null);
        setSelectedSignatureId('');
        const calRes = await fetch(`${apiBaseUrl}/email-send-preference`, {
          method: 'PUT',
          headers,
          body: JSON.stringify({ googleCalendarUseSecondAccount: calendarUseSecondAccount }),
        });
        if (!calRes.ok) throw new Error('Failed to save calendar preference');
        setServerCalendarUseSecondAccount(calendarUseSecondAccount);
      } else {
        const res = await fetch(`${apiBaseUrl}/email-send-preference`, {
          method: 'PUT',
          headers,
          body: JSON.stringify({
            connectionId: connParsed.connectionId,
            provider: connParsed.provider,
            googleCalendarUseSecondAccount: calendarUseSecondAccount,
          }),
        });
        if (!res.ok) throw new Error('Failed to save default');
        setDefaultPreference(connParsed);
        // Save default signature for this connection
        const sigRes = await fetch(`${apiBaseUrl}/email-signature-preference`, {
          method: 'PUT',
          headers,
          body: JSON.stringify({
            connectionId: connParsed.connectionId,
            signatureId: selectedSignatureId || null,
          }),
        });
        if (!sigRes.ok) throw new Error('Failed to save default signature');
        // Refresh context so is_default is updated in local state
        await fetchContext();
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }, [apiBaseUrl, selectedValue, selectedSignatureId, calendarUseSecondAccount, fetchContext]);

  const hasChanges = computeHasChanges({
    selectedValue,
    parsed,
    defaultPreference,
    selectedSignatureId,
    currentDefaultSignatureId,
    calendarUseSecondAccount,
    serverCalendarUseSecondAccount,
  });

  return {
    connections,
    selectedValue,
    setSelectedValue,
    parsed,
    signaturesForConnection,
    selectedSignatureId,
    setSelectedSignatureId,
    calendarUseSecondAccount,
    setCalendarUseSecondAccount,
    loading,
    saving,
    saved,
    error,
    hasChanges,
    save,
  };
}
